/* eslint-disable max-lines -- We will have to deal with this, but it's types */
import type { Point } from "geojson";
import type * as H from "history";
import type { LDFlagSet } from "launchdarkly-js-sdk-common";
import type { RouteComponentProps } from "react-router-dom";
import type { DateString, MCTDateProp } from "./dates";
import type { SetNonNullable } from "type-fest";

export enum UserRole {
  ANON_BENEFICIARY = "anon",
  AUTH_BENEFICIARY = "beneficiary",
  CSR = "csr",
}

export enum UserType {
  ANON_BENEFICIARY = "guest",
  AUTH_BENEFICIARY = "beneficiary",
  CSR = "csr",
}

export interface LdFlagsResponse {
  flags: LDFlagSet;
  "secure-mode-hash": string;
}

/**
 * @see {@link https://imp.medicare.gov/plan-compare/guide/external_dependencies/launchdarkly/use.html?highlight=year%20parts#launchdarkly-mct-current-plan-year-and-mct-year-part-flags|LaunchDarkly Year Part flags}
 */
export enum MctYearPartFlag {
  OUTSIDE_OPEN_ENROLLMENT = "OOE",
  PUBLIC_PREVIEW = "PP",
  OPEN_ENROLLMENT = "OE",
  OPEN_ENROLLMENT_NEXT_YEAR_ONLY = "OE_NYO",
}
export interface PharmaciesRequest {
  zipcode: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  name?: string;
  radius_miles?: number;
  count: number;
  page: number;
}

export interface InNetworkPharmaciesRequest extends PharmaciesRequest {
  preferred_only?: boolean;
}
export interface AbstractBenefitFrequencyCost {
  days_30: string | null;
  days_90: string | null;
  days_any: string | null;
}

export interface AbstractBenefitPhase {
  footnote: string;
  tiers: AbstractBenefitTier[];
}

export type AbstractBenefits = {
  catastrophic: AbstractBenefitPhase | null;
  coverage_gap: AbstractBenefitPhase | null;
  initial_coverage: AbstractBenefitPhase | null;
  lis: AbstractBenefitPhase | null;
  platino: AbstractBenefitPhase | null;
};

export type AbstractBenefitKey = keyof AbstractBenefits;

export type CostShareTier =
  | "COST_SHARE_TIER_ALL_FORMULARY_DRUGS_TIER"
  | "COST_SHARE_TIER_0_DOLLAR_DRUGS"
  | "COST_SHARE_TIER_BRAND_DRUGS"
  | "COST_SHARE_TIER_BRAND_NAME_DRUGS"
  | "COST_SHARE_TIER_BRAND"
  | "COST_SHARE_TIER_EXCLUDED_DRUGS"
  | "COST_SHARE_TIER_GENERIC_DRUGS"
  | "COST_SHARE_TIER_GENERIC"
  | "COST_SHARE_TIER_INJECTABLE_DRUGS"
  | "COST_SHARE_TIER_NON_MEDICARE_OTC_DRUGS"
  | "COST_SHARE_TIER_NON_MEDICARE_RX_DRUGS"
  | "COST_SHARE_TIER_NON_MEDICARE_RX_OTC_DRUGS"
  | "COST_SHARE_TIER_NON_PREFERRED_BRAND_DRUGS"
  | "COST_SHARE_TIER_NON_PREFERRED_BRAND"
  | "COST_SHARE_TIER_NON_PREFERRED_DRUG"
  | "COST_SHARE_TIER_PREFERRED_BRAND_DRUGS"
  | "COST_SHARE_TIER_PREFERRED_BRAND"
  | "COST_SHARE_TIER_PREFERRED_GENERIC_DRUGS"
  | "COST_SHARE_TIER_PREFERRED_GENERIC"
  | "COST_SHARE_TIER_PREFERRED_SPECIALTY_TIER"
  | "COST_SHARE_TIER_SELECT_CARE_DRUGS"
  | "COST_SHARE_TIER_SELECT_DIABETIC_DRUGS"
  | "COST_SHARE_TIER_SPECIALTY_TIER"
  | "COST_SHARE_TIER_SUPPLEMENTAL_BRAND_AND_GENERIC_DRUGS"
  | "COST_SHARE_TIER_SUPPLEMENTAL_DRUGS"
  | "COST_SHARE_TIER_VACCINES";

export interface AbstractBenefitTier {
  general: string | null;
  label: CostShareTier;
  preferred_mail_order: AbstractBenefitFrequencyCost | null;
  preferred_retail: AbstractBenefitFrequencyCost | null;
  standard_mail_order: AbstractBenefitFrequencyCost | null;
  standard_retail: AbstractBenefitFrequencyCost | null;
  tier_row_order: number;
}

export enum AccessibilityFormat {
  Default = "",
  Braille = "Braille",
  LargePrint = "LargePrint",
  AudioCd = "AudioCD",
  DataCd = "DataCD",
}

export interface Action {
  payload?: unknown;
  type: ActionType;
}

export enum ActionType {
  ADD_BENEFICIARY = "ADD_BENEFICIARY",
  ADD_CSR = "ADD_CSR",
  ADD_PHARMACY = "ADD_PHARMACY",
  ADD_PRESCRIPTION = "ADD_PRESCRIPTION",
  ADD_PROVIDER = "ADD_PROVIDER",
  DELETE_PHARMACY = "DELETE_PHARMACY",
  DELETE_PRESCRIPTION = "DELETE_PRESCRIPTION",
  DELETE_PROVIDER = "DELETE_PROVIDER",
  DISMISS_ALERT = "DISMISS_ALERT",
  EDIT_PRESCRIPTION = "EDIT_PRESCRIPTION",
  EVENT_TRACKED = "EVENT_TRACKED",
  RESET_PHARMACIES = "RESET_PHARMACIES",
  /**
   * This action deletes `cms-mct-store` (the store), including the `beneficiary`
   * object. It will be recreated with default values (and no `beneficiary`)
   *
   * Optional `action.payload` value, `logout: true` indicates only `UserLanguage`
   * should be preserved
   */
  RESET_STATE = "RESET_STATE",
  RESTORE_DISMISSED_ALERT = "RESTORE_DISMISSED_ALERT",
  SAVE_PHARMACIES = "SAVE_PHARMACIES",
  SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS = "SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS",
  SET_PHARMACIES = "SET_PHARMACIES",
  SET_PRESCRIPTIONS = "SET_PRESCRIPTIONS",
  SET_TEALIUM_LOADED = "SET_TEALIUM_LOADED",
  UPDATE_COMPARE_LIST = "UPDATE_COMPARE_LIST",
  UPDATE_COUNTY = "UPDATE_COUNTY",
  UPDATE_CSR_GUEST_ACCESS = "UPDATE_CSR_GUEST_ACCESS",
  UPDATE_CW_COVERAGE = "UPDATE_CW_COVERAGE",
  UPDATE_DEGRADED_INTEGRATIONS = "UPDATE_DEGRADED_INTEGRATIONS",
  UPDATE_DEMOGRAPHICS = "UPDATE_DEMOGRAPHICS",
  UPDATE_ENROLL_DATA = "UPDATE_ENROLL_DATA",
  UPDATE_FIPS = "UPDATE_FIPS",
  UPDATE_FUTURE_LIS = "UPDATE_FUTURE_LIS",
  UPDATE_LANGUAGE = "UPDATE_LANGUAGE",
  UPDATE_LAST_ACTIVITY = "UPDATE_LAST_ACTIVITY",
  UPDATE_LD_FLAGS = "UPDATE_LD_FLAGS",
  UPDATE_LIS = "UPDATE_LIS",
  UPDATE_MAPBOX_PHARMACY_SUGGESTIONS = "UPDATE_MAPBOX_PHARMACY_SUGGESTIONS",
  UPDATE_MAX_PROVIDER_SEARCH_RADIUS = "UPDATE_MAX_PROVIDER_SEARCH_RADIUS",
  UPDATE_MBP_HANDOFF_FAILED = "UPDATE_MBP_HANDOFF_FAILED",
  UPDATE_MBP_REFERRER_URI = "UPDATE_MBP_REFERRER_URI",
  UPDATE_NEW_TO_MEDICARE_ALERT_INFO = "UPDATE_NEW_TO_MEDICARE_ALERT_INFO",
  UPDATE_PHARMACY_SEARCH_ADDRESS = "UPDATE_PHARMACY_SEARCH_ADDRESS",
  UPDATE_PHARMACY_NETWORK_STATUS = "UPDATE_PHARMACY_NETWORK_STATUS",
  UPDATE_PHARMACY_TYPE = "UPDATE_PHARMACY_TYPE",
  UPDATE_PLAN_TYPE = "UPDATE_PLAN_TYPE",
  UPDATE_ROUTE_PARAMS = "UPDATE_ROUTE_PARAMS",
  UPDATE_SEARCH_RESULTS_FILTERS = "UPDATE_SEARCH_RESULTS_FILTERS",
  UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS = "UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS",
  UPDATE_SEE_DRUG_COSTS = "UPDATE_SEE_DRUG_COSTS",
  UPDATE_SELECTED_PLAN = "UPDATE_SELECTED_PLAN",
  UPDATE_SNP_FILTERS = "UPDATE_SNP_FILTERS",
  UPDATE_WIZARD_ANSWER = "UPDATE_WIZARD_ANSWER",
  UPDATE_YEAR = "UPDATE_YEAR",
  UPDATE_ZIPCODE = "UPDATE_ZIPCODE",
  UPDATE_TOTAL_SEARCH_RESULTS = "UPDATE_TOTAL_SEARCH_RESULTS",
}

export interface AdditionalSupplementalBenefits {
  mmp_benefits: OtherBenefitCategory[];
  other_benefits: OtherBenefitCategory[];
  special_benefits: OtherBenefitCategory[];
}

export enum AdditionalSupplementalBenefitCoverage {
  SB_COVERAGE_NOT_COVERED = "SB_COVERAGE_NOT_COVERED",
  SB_COVERAGE_SOME_COVERAGE = "SB_COVERAGE_SOME_COVERAGE",
}

export interface ApiErrorMessage {
  errorResponse: ApiErrorResponse;
  status: number;
  statusText: string;
  type: string;
  url: string;
}

export interface ApiErrorResponse {
  code?: number;
  details?: ApiErrorResponseDetail[];
  error: string;
  message?: string;
}

export interface ApiErrorResponseDetail {
  "@type": string;
  request_id: string;
  serving_data: ApiResponseServingData | string;
}

export interface CwPremiumRanges {
  request_id: string;
  pdp_premium_min: number | null;
  pdp_premium_max: number | null;
  ma_premium_min: number | null;
  ma_premium_max: number | null;
  medigap_premium_min: number | null;
  medigap_premium_max: number | null;
}

interface ApiResponseQueryObject {
  contract_id: string;
  plan_id: string;
  segment_id: string;
  year: string;
}

export interface ApiResponseServingData {
  query?: ApiResponseQueryObject;
}

export type GenericDrug = {
  rxcui: string; // "83367"
  name: string; // "atorvastatin"
  branded_generic: boolean;
};

// * Api Bases

/** base type for drug autocomplete and letter search endpoints */
export type DrugBase = {
  generic: GenericDrug | null;
  insulin: boolean;
  is_generic: boolean;
  name: string; // "Acthar"
  pap_drug_id: string;
  rxcui: string; // "1040019"
};

/** drug that comes from MCTAPI autocomplete endpoint*/
export type AutocompleteDrug = DrugBase & {
  generics: GenericDrug[];
};

export type AutocompleteDrugWithGeneric = SetNonNullable<
  AutocompleteDrug,
  "generic"
>;

/** drug that comes from MCTAPI letter search endpoint*/
export type DrugByLetter = DrugBase;

/** base type for pap autocomplete and letter search endpoints */
export type PapDrugBase = {
  id: number; // 4323
  name: string; // "Welireg"
};

/** drug that comes from MCTAPI pap autocomplete endpoint*/
export type AutocompletePapDrug = PapDrugBase;

/** drug that comes from MCTAPI letter pap search endpoint*/
export type PapDrugByLetter = PapDrugBase;

// * Front End Types

/** in DrugAutocomplete.tsx, we support both variations of autocomplete drug. This type exists for developer ease.*/
export type AnyAutocompleteDrug = AutocompleteDrug | AutocompletePapDrug;

/**  in AlphabeticalDrugList.tsx, we support both variations. This type exists for developer ease. */
export type AnyDrugByLetter = DrugByLetter | PapDrugByLetter;
export interface BasePlan {
  readonly annual_deductible: string | number;
  readonly calculated_monthly_premium: number;
  readonly category: PlanCategory;
  readonly contract_id: string;
  readonly contract_year: string;
  readonly drug_plan_deductible: number;
  readonly enrollment_opt_in_status: boolean;
  readonly high_performing: boolean;
  readonly id: number;
  readonly lis: LIS;
  readonly low_performing: boolean;
  readonly msa_annual_deposit: number;
  readonly name_esp: string;
  /** Name of plan, access with getPlanName() helper */
  readonly name: string;
  readonly organization_name: string;
  // @TODO: Figure out the typing for star ratings: null, undefined, and StarRating?
  readonly overall_star_rating: StarRating;
  readonly package_services: PackageServices | null;
  readonly partb_premium_reduction: number;
  readonly partc_premium: number;
  readonly partd_premium: number;
  readonly plan_id: string;
  readonly plan_type: PlanType;
  readonly redactions: Redaction[];
  readonly segment_id: string;
  readonly silver_sneakers: boolean;
  readonly snp_type: PlanSnpType;
  readonly total_remaining_premium: number;
  readonly url: string;
  readonly worldwide_emergency: boolean;
  readonly aip: boolean | null;
  readonly dsnp_integration_level: "UNSPECIFIED" | "CO" | "HIDE" | "FIDE";
  readonly provider_coverage: PlanInfoProviderCoverage;
}

/**
 * @example "2021-H5652-002-0"
 * Matches
 * `${plan.contract_year}-${plan.contract_id}-${plan.plan_id}-${plan.segment_id}`
 */
export type PlanLongId = `${string}-${string}-${string}-${string}`;

export enum NewToMedicareStatus {
  UNDEFINED = "NEW_TO_MEDICARE_UNDEFINED",
  YES = "NEW_TO_MEDICARE_YES",
  NO = "NEW_TO_MEDICARE_NO",
}

export interface BeneMetaData {
  beneficiary_key: string;
  is_csr: boolean;
  is_logged_in: boolean;
  is_synthetic: boolean;
  last_login_date?: string | null;
}

export interface Beneficiary {
  activity_id: string;
  birthdate: string; // YYYY-MM-DD
  csr_id: string;
  coverage_current: ExtendedCoverageInfo[];
  coverage_future: ExtendedCoverageInfo[];
  coverage_rollover: BeneInfoCoverageRollover[];
  email: string;
  employer_coverage_current: ExtendedCoverageInfo[];
  employer_coverage_future: ExtendedCoverageInfo[];
  firstname: string[];
  future_lis_level: LowIncomeSubsidyStatus;
  future_lis_status_start_date: string; // YYYY-MM-DD
  gender: Gender;
  hicn: string;
  lastname: string;
  lis_level: LowIncomeSubsidyStatus;
  mailing_address: MailingAddress;
  mbi: string;
  medicare_info: MedicareInfo;
  meta_data: BeneMetaData;
  pdes: PDE[];
  request_id: string;
  ssn: string;
  // Deprecated
  /**
   * @deprecated  new_to_medicare attribute has nothing to do with the current NtM logic.
   * It predates it and has a completely different set of criteria for being true
   * */
  new_to_medicare: NewToMedicareStatus;
  /** @deprecated use medicare_info */
  part_a_start: string;
  /** @deprecated use medicare_info */
  part_b_start: string;
}

export interface MedicarePartInfo {
  start_date: string; // YYYY-MM-DD or ""
  stop_date: string; // YYYY-MM-DD or ""
}

// beneinfo/userinfo.go#getMedicareInfo
export interface MedicareInfo {
  new_to_medicare: NewToMedicareStatus;
  // iep = Initial Enrollment Period
  iep_start_date: string; // YYYY-MM-DD or ""
  iep_end_date: string; // YYYY-MM-DD or ""
  early_beneficiary: boolean;
  part_a: MedicarePartInfo;
  part_b: MedicarePartInfo;
}

export interface MailingAddress {
  address_1: string;
  address_2: string;
  address_3: string;
  city: string;
  fips_state_county: string;
  state: string;
  street_address: string[];
  zipcode: string;
}

export interface BeneficiaryAndApiHeaders {
  beneficiary: Beneficiary | undefined;
  headers: Headers;
}

export interface BenefitItemProps {
  benefit: PlanInfoBenefitCategory;
  label: string | JSX.Element;
  /**
   * If a `service` isn't included in `benefitItemProps`, (may be purposefully omitted
   * for getting cost-sharing data from a plan's `package_benefits`), you can add a
   * `maBenefitService` field for use specifically with helpers that derive cost-
   * sharing data from `ma_benefits`
   */
  maBenefitService?: PlanInfoBenefitService;
  mobileLabelOnly?: boolean;
  plan: Plan;
  service?: PlanInfoBenefitService;
  suppressed?: boolean;
}

export interface CallbackOutput {
  code: string;
  state: number;
}
export interface ClientErrorMessage {
  apiError?: string;
  cause: string;
}

export interface CSR {
  activityId: string;
  id: string;
}

export interface County {
  fips: string;
  name: string;
  state: string;
}

export type CoverageInfo = IdParts;

export interface DrugDosage {
  form: string;
  has_duplicate_name: boolean;
  name: string;
  packages: DrugPackage[];
  rxcui: string;
  strength_units: string;
  strength: string;
}

export interface DrugDosageInfo {
  commonDosage: DrugDosage | null;
  dosages: DrugDosage[];
}

export type CostPhaseKey =
  | "catastrophic_cost"
  | "deductible_cost"
  | "full_cost"
  | "gap_cost"
  | "initial_cost";

export type DrugCostPhases = Record<CostPhaseKey, number | string>;

export interface DrugCost extends DrugCostPhases {
  cost_sharing_overide: boolean;
  coverage_reason: DrugCostCoverageReason;
  covered: boolean;
  default_price_used: boolean;
  estimated_yearly_total: number;
  frequency: PrescriptionFrequency;
  has_deductible: boolean;
  ndc: string;
  quantity: number | string;
  tier: number | null;
}

export interface DrugCostInfo {
  costs: DrugCosts[];
  /**
   * @TODO - this should not be flagged as optional after the PR for
   * https://jira.cms.gov/browse/MCT-9791 is merged and in PROD
   * @see https://github.com/CMSgov/oc-mct-api/pull/2638
   */
  cumulative_meets_annual_cap?: boolean;
  excluded_drugs: string[];
  lowest_mail_total: number | string;
  lowest_retail_total: number | string;
  lowest_mail_valid: boolean;
  lowest_retail_valid: boolean;
  plan: FullPlanId;
  restrictions: {
    acip: boolean;
    conditions: string[];
    free_first_fill: boolean;
    home_infusion: boolean;
    insulin_price_cap: boolean;
    limited_distribution: boolean;
    ndc: string;
    opioid: boolean;
    partial_gap: boolean;
    vaccine: boolean;
    is_paxlovid?: boolean;
  }[];
}

export enum DrugCostCoverageReason {
  COVERED = "COVERED",
  OUT_OF_NETWORK = "OUT_OF_NETWORK",
  NOT_IN_FORMULARY = "NOT_IN_FORMULARY",
  FREQUENCY_NOT_COVERED = "FREQUENCY_NOT_COVERED",
}

export interface M3PMonthlyCosts {
  /**
   * Date of the first day of each month, returned from MCTAPI in the form of `YYYY-MM-DD`
   * (`DD` is always `01`, but leaving the typing open.)
   *
   * Month needs to be extracted and turned into a translated month name string
   */
  date: `${number}${number}${number}${number}-${number}${number}-${number}${number}`;
  mppp_total: number;
  part_d_only_total: number;
}

export interface MedicarePrescriptionPaymentPlan {
  estimated_monthly_costs: M3PMonthlyCosts[];
  estimated_yearly_mppp_cost: number;
  estimated_yearly_part_d_only_cost: number;
  meets_annual_cap: boolean;
}

/**
 * M3pData is a Record mapping `npi`s to M3P data from MCTAPI
 * For MO, the `npi` is "normalized" to "mail-order"
 */
export type M3pData = Record<string, MedicarePrescriptionPaymentPlan>;

export interface DrugCosts {
  drug_costs: DrugCost[];
  estimated_monthly_costs: EstimatedMonthlyDrugCost[];
  estimated_yearly_total: number;
  in_network: boolean;
  mail_order: boolean;
  medicare_prescription_payment_plan: MedicarePrescriptionPaymentPlan | null;
  npi: string;
  phase_information: DrugPhaseInformation;
  preferred: boolean;
  ltc: boolean;
}

// response from /drugs/cost API
export interface DrugCostsResponse {
  full_year: boolean;
  plans: DrugCostInfo[];
  request_id: string;
}

export type DrugCoverageFilterOption = "includes" | "excludes";

export enum DrugFootnote {
  CONDITION = "condition",
  EXCLUDED_DRUG = "excluded_drug",
  FREE_FIRST_FILL = "free_first_fill",
  HOME_INFUSION = "home_infusion",
  LIMITED_DISTRIBUTION = "limited_distribution",
  OPIOID = "opioid",
  PARTIAL_GAP = "partial_gap",
  VACCINE = "vaccine",
  NOT_IN_FORMULARY = "not_in_formulary",
  DEFAULT_PRICE = "default_price_used",
  FREQUENCY_NOT_COVERED = "frequency_not_covered",
  PAXLOVID = "paxlovid",
}

export interface Biosimilar {
  displayName?: string;
  is_interchangeable: boolean;
  name: string;
  on_formulary: boolean;
  rxcui: string;
}

export interface DrugInfo {
  ndc: string;
  on_formulary?: boolean;
  prior_auth: boolean;
  quantity_limit_amount: number;
  quantity_limit_days: number;
  quantity_limit: boolean;
  step_therapy: boolean;
  tier: number;
  biosimilars: Biosimilar[];
}

export type DrugPackageType =
  | "PACKAGE_TYPE_AMPULE"
  | "PACKAGE_TYPE_ATOMIZER"
  | "PACKAGE_TYPE_BAG"
  | "PACKAGE_TYPE_BLISTER"
  | "PACKAGE_TYPE_BOTTLE"
  | "PACKAGE_TYPE_BOX"
  | "PACKAGE_TYPE_CAN"
  | "PACKAGE_TYPE_CARTRIDGE"
  | "PACKAGE_TYPE_CUP"
  | "PACKAGE_TYPE_NEEDLE_CARTRIDGE"
  | "PACKAGE_TYPE_DISPENSABLE_PACK"
  | "PACKAGE_TYPE_DRUM"
  | "PACKAGE_TYPE_FLEXIBLE_CONTAINER"
  | "PACKAGE_TYPE_GLASS_CONTAINER"
  | "PACKAGE_TYPE_INHALER"
  | "PACKAGE_TYPE_JAR"
  | "PACKAGE_TYPE_PACK"
  | "PACKAGE_TYPE_PUMP_BOTTLE"
  | "PACKAGE_TYPE_PLASTIC_CONTAINER"
  | "PACKAGE_TYPE_PUNCHCARD"
  | "PACKAGE_TYPE_PACKAGE"
  | "PACKAGE_TYPE_PACKET"
  | "PACKAGE_TYPE_PEN"
  | "PACKAGE_TYPE_ROLL"
  | "PACKAGE_TYPE_SPRAY_BOTTLE"
  | "PACKAGE_TYPE_SACHET"
  | "PACKAGE_TYPE_STICK"
  | "PACKAGE_TYPE_STRIP"
  | "PACKAGE_TYPE_SYRINGE"
  | "PACKAGE_TYPE_TUBE"
  | "PACKAGE_TYPE_VIAL";

export interface DrugPackage {
  default_quantity_30: number;
  default_quantity_90: number;
  alternate_ndc: string;
  ndc: string;
  ndc_status: string;
  quantity: number;
  size: number;
  type: DrugPackageType;
  unit: DrugPackageUnit;
  use_quantity: boolean;
  volume: number;
}

export enum DrugPackageUnit {
  "N/A" = "N/A",
  EA = "EA",
  GM = "GM",
  ML = "ML",
}

export interface DrugPhaseInformation {
  catastrophic_start: string;
  deductible_amount: number;
  gap_start: string;
  initial_coverage_start: string;
}

export enum EffectiveSubsidyLevel {
  EFFECTIVE_SUBSIDY_LEVEL_FULL_DUAL = "EFFECTIVE_SUBSIDY_LEVEL_FULL_DUAL",
  EFFECTIVE_SUBSIDY_LEVEL_FULL_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_FULL_SUBSIDY",
  EFFECTIVE_SUBSIDY_LEVEL_NO_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_NO_SUBSIDY",
  EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_DUAL = "EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_DUAL",
  EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_SUBSIDY",
}

export enum EffectiveSubsidyPercent {
  EFFECTIVE_SUBSIDY_PERCENT_100 = "EFFECTIVE_SUBSIDY_PERCENT_100",
  EFFECTIVE_SUBSIDY_PERCENT_25 = "EFFECTIVE_SUBSIDY_PERCENT_25",
  EFFECTIVE_SUBSIDY_PERCENT_50 = "EFFECTIVE_SUBSIDY_PERCENT_50",
  EFFECTIVE_SUBSIDY_PERCENT_75 = "EFFECTIVE_SUBSIDY_PERCENT_75",
}

export enum Race {
  RACE_I_CHOOSE_NOT_TO_ANSWER = "RACE_I_CHOOSE_NOT_TO_ANSWER",
  RACE_BLACK_OR_AFRICAN_AMERICAN = "RACE_BLACK_OR_AFRICAN_AMERICAN",
  RACE_AMERICAN_INDIAN_OR_ALASKA_NATIVE = "RACE_AMERICAN_INDIAN_OR_ALASKA_NATIVE",
  RACE_ASIAN_INDIAN = "RACE_ASIAN_INDIAN",
  RACE_CHINESE = "RACE_CHINESE",
  RACE_FILIPINO = "RACE_FILIPINO",
  RACE_JAPANESE = "RACE_JAPANESE",
  RACE_KOREAN = "RACE_KOREAN",
  RACE_VIETNAMESE = "RACE_VIETNAMESE",
  RACE_OTHER_ASIAN = "RACE_OTHER_ASIAN",
  RACE_NATIVE_HAWAIIAN = "RACE_NATIVE_HAWAIIAN",
  RACE_SAMOAN = "RACE_SAMOAN",
  RACE_GUAMANIAN_OR_CHAMORRO = "RACE_GUAMANIAN_OR_CHAMORRO",
  RACE_OTHER_PACIFIC_ISLANDER = "RACE_OTHER_PACIFIC_ISLANDER",
  RACE_WHITE = "RACE_WHITE",
}

export enum Ethnicity {
  ETHNICITY_I_CHOOSE_NOT_TO_ANSWER = "ETHNICITY_I_CHOOSE_NOT_TO_ANSWER",
  ETHNICITY_PUERTO_RICAN = "ETHNICITY_PUERTO_RICAN",
  ETHNICITY_ANOTHER_HISPANIC_LATINO_OR_SPANISH_ORIGIN = "ETHNICITY_ANOTHER_HISPANIC_LATINO_OR_SPANISH_ORIGIN",
  ETHNICITY_MEXICAN_OR_MEXICAN_AMERICAN_OR_CHICANO_CHICANA = "ETHNICITY_MEXICAN_OR_MEXICAN_AMERICAN_OR_CHICANO_CHICANA",
  ETHNICITY_CUBAN = "ETHNICITY_CUBAN",
  ETHNICITY_NOT_HISPANIC_OR_LATINO_LATINA_OR_SPANISH_ORIGIN = "ETHNICITY_NOT_HISPANIC_OR_LATINO_LATINA_OR_SPANISH_ORIGIN",
}

export enum IndividualRepresentativeRelationship {
  LeftBlank = "INDIVIDUAL_REP_RELATIONSHIP_LEFT_BLANK",
  Agent = "INDIVIDUAL_REP_RELATIONSHIP_AGENT",
  Broker = "INDIVIDUAL_REP_RELATIONSHIP_BROKER",
  Counselor = "INDIVIDUAL_REP_RELATIONSHIP_SHIP_COUNSELOR",
  AuthorizedRepresentative = "INDIVIDUAL_REP_RELATIONSHIP_AUTHORIZED_REPRESENTATIVE",
  Other = "INDIVIDUAL_REP_RELATIONSHIP_OTHER",
  Self = "INDIVIDUAL_REP_RELATIONSHIP_SELF",
}
export interface IndividualRepresentative {
  name: string | null;
  relationshipCode: IndividualRepresentativeRelationship | null;
  /** int64 */
  nationalProducerNumber: string | null;
}

export enum Envs {
  imp = "imp",
  local = "local",
  prod = "prod",
  test = "test",
  dev = "dev",
}

export enum TealiumEnvironment {
  DEV = "dev",
  QA = "qa",
  PROD = "prod",
}

export interface EstimatedMonthlyDrugCost {
  catastrophic: number;
  date: string;
  deductible: number;
  gap: number;
  initial: number;
  total: number;
}

export interface GlobalSessionInfo {
  exp: number;
  maximum_exp: number;
}

export interface ExtendedCoverageInfo extends IdParts {
  coverage_year: string;
  name: string;
  name_esp: string;
  enrollment_effective_date: string;
  contract_effective_date: string;
  disenrollment_date: string;
  plan_type: PlanType;
}

export interface BeneInfoCoverageRollover extends IdParts {
  plan_name: string;
  plan_name_esp: string;
  source: string;
  status: string;
  plan_type: PlanType;
}

export type FeaturedPlanDetails = {
  plan: SearchResultPlan | OecStatus | ExtendedCoverageInfo | undefined;
  statusMessages: {
    primary: string | JSX.Element | undefined;
    secondary?: string | JSX.Element | undefined;
  };
  coverageInfo?: string | JSX.Element | undefined;
  isCurrentCoverage?: boolean;
};

export type FeaturedPlanAndCoverages = {
  featuredPlanDetails: FeaturedPlanDetails;
  planCardCurrentCoverage: SearchResultPlan | undefined;
  planCardOecStatuses: OecStatuses;
  planCardFuturePlan: ExtendedCoverageInfo | undefined;
};

export enum ThirdPartyIntegrations {
  BEDAP = "BEDAP",
  BLUE_BUTTON = "Blue Button",
}

export interface FullPlanId extends Omit<CoverageInfo, "coverage_year"> {
  contract_year: string;
}

export interface RolloverCoveragePlan extends Omit<
  CoverageInfo,
  "coverage_year"
> {
  contract_year: string;
  status: RolloverStatusV1;
  plan_status?: RolloverPlanStatus;
}

/** Values from /plan/next */
export enum RolloverStatusV1 {
  Renewal = "RENEWAL",
  RenewalSar = "RENEWAL_SAR",
  RenewalSae = "RENEWAL_SAE",
  New = "NEW",
  Consolidated = "CONSOLIDATED",
  Terminated = "TERMINATED",
}

/** Values from /beneinfo/rollover-coverage */
export enum RolloverStatus {
  Rollover = "ROLLOVER_STATUS_ROLLOVER",
  OriginalMedicare = "ROLLOVER_STATUS_ORIGINAL_MEDICARE",
  InitialContract = "ROLLOVER_STATUS_INITIAL_CONTRACT",
  NotFound = "ROLLOVER_STATUS_NOT_FOUND",
  NewPlan = "ROLLOVER_STATUS_NEW_PLAN",
  PlanException = "ROLLOVER_STATUS_PLAN_EXCEPTION",
}

// Only used for /beneinfo/rollover-coverage
export enum RolloverPlanStatus {
  Renewal = "PLAN_STATUS_RENEWAL",
  ServiceAreaExpansion = "PLAN_STATUS_SERVICE_AREA_EXPANSION",
  ServiceAreaReduction = "PLAN_STATUS_SERVICE_AREA_REDUCTION",
  PlanTerminated = "PLAN_STATUS_PLAN_TERMINATED",
  ContractTerminated = "PLAN_STATUS_CONTRACT_TERMINATED",
  MarxException = "PLAN_STATUS_MARX_EXCEPTION",
  NewPlan = "PLAN_STATUS_NEW_PLAN",
  InitialContract = "PLAN_STATUS_INITIAL_CONTRACT",
  Disenrolled = "PLAN_STATUS_DISENROLLED",
  Unknown = "PLAN_STATUS_UNKNOWN",
}

export enum Gender {
  FEMALE = "GENDER_FEMALE",
  MALE = "GENDER_MALE",
  UNKNOWN = "GENDER_NA",
}

export interface IdParts {
  contract_id: string;
  contract_year?: string;
  coverage_year?: string;
  plan_id: string;
  segment_id: string;
}

export interface PlanIdParts extends IdParts {
  contract_year: string;
}

export type IdPartsWithYear = IdParts & {
  year?: string | undefined;
};

export interface Indexable {
  index: number;
}

export interface LIS {
  readonly level_100: number;
  readonly level_25: number;
  readonly level_50: number;
  readonly level_75: number;
}

export enum LowIncomeSubsidyAmount {
  Help0 = "LEVEL_0",
  Help25 = "LEVEL_25",
  Help50 = "LEVEL_50",
  Help75 = "LEVEL_75",
  HelpNA = "LEVEL_NA",
  HelpUnsure = "LEVEL_100",
}

export enum LowIncomeSubsidyOption {
  Medicaid = "LIS_MEDICAID",
  MSP = "LIS_MSP",
  None = "LIS_NONE",
  SS = "LIS_SS",
  SSI = "LIS_SSI",
  Unsure = "LIS_UNSURE",
}

export enum LowIncomeSubsidyStatus {
  LIS_LEVEL_1A = "LIS_LEVEL_1A",
  LIS_LEVEL_1A_DN = "LIS_LEVEL_1A_DN",
  LIS_LEVEL_1B = "LIS_LEVEL_1B",
  LIS_LEVEL_1C = "LIS_LEVEL_1C",
  LIS_LEVEL_2 = "LIS_LEVEL_2",
  LIS_LEVEL_3 = "LIS_LEVEL_3",
  LIS_LEVEL_4_100 = "LIS_LEVEL_4_100",
  LIS_LEVEL_4_25 = "LIS_LEVEL_4_25",
  LIS_LEVEL_4_50 = "LIS_LEVEL_4_50",
  LIS_LEVEL_4_75 = "LIS_LEVEL_4_75",
  LIS_NO_HELP = "LIS_NO_HELP",
}

export interface MailOrderNetworkStatus {
  inNetwork: boolean;
  preferred: boolean;
}

export enum MctEvent {
  costsLoaded = "costs_loaded",
}

export enum MctToastType {
  BENE_FAILED_LOGIN = "bfl",
  CSR_BENE_FAIL = "cbf",
  LOGGED_OUT = "lo",
  SESSION_EXPIRED = "se",
}

export interface MenuItem {
  id?: string;
  onClick?: () => void;
  title: string;
  url?: string;
}

export interface NetworkCostDescriptor {
  readonly authorization_required: boolean;
  readonly cost_share: string;
  readonly plan_limits: boolean;
  readonly referral_required: boolean;
}

export interface NetworkCostDescriptors {
  /* "NETWORK_TYPE_IN_NETWORK" */
  readonly [x: string]: NetworkCostDescriptor;
}

/** Pending Enrollment Status */
export interface OecStatus {
  confirmation_number: string;
  contract_id: string;
  download_date: string;
  downloaded: boolean;
  plan_id: string;
  plan_name_esp: string;
  plan_name: string;
  plan_type: PlanType;
  segment_id: string;
  year: string;
}

/**
 * Pending Enrollment Statuses, created by `usePendingEnrollments`
 * - `undefined` means we have not called the endpoint.
 * - `null` means that we called the endpoint and there was an error or no result.
 * */
export interface OecStatuses {
  currentYear?: OecStatus | null;
  nextYear?: OecStatus | null;
}

export interface OecStatusResponse {
  statuses: OecStatus[];
}

export interface OptionalBenefitPackage {
  benefits: string[];
  readonly deductible: string;
  readonly monthly_premium: string;
  readonly package_number: number;
}

export interface PlanCardSupplementalBenefitExtraInfo {
  authorization_required: boolean;
  coins_max_pct: number | null;
  coins_min_pct: number | null;
  copay_max: number | null;
  copay_min: number | null;
  oon_coins_max_pct: number | null;
  oon_coins_min_pct: number | null;
  oon_copay_max: number | null;
  oon_copay_min: number | null;
  oon_max_plan_amount: number | null;
  oon_deductible_amount: number | null;
  referral_required: boolean;
}

export interface OtherBenefit {
  benefit: string;
  coverage: string;
  /**
   * @see https://github.com/CMSgov/oc-mct-api/blob/5fa146213e8453cb65e946b7cc2946b1d2dc2459/planinfo/plan_details.go#L378-L381
   */
  info: PlanCardSupplementalBenefitExtraInfo | null;
}

export interface OtherBenefitCategory {
  benefits: OtherBenefit[];
  category: string;
}

export enum PaceDay {
  DAY_SUNDAY = "DAY_SUNDAY",
  DAY_MONDAY = "DAY_MONDAY",
  DAY_TUESDAY = "DAY_TUESDAY",
  DAY_WEDNESDAY = "DAY_WEDNESDAY",
  DAY_THURSDAY = "DAY_THURSDAY",
  DAY_FRIDAY = "DAY_FRIDAY",
  DAY_SATURDAY = "DAY_SATURDAY",
}

export interface PaceOfficeHours {
  close_time: string;
  day: PaceDay;
  open: boolean;
  open_all_day: boolean;
  open_time: string;
  time_zone: PaceTimeZone;
}

export interface PacePhone {
  number: string;
  extension: string;
}

export interface PacePhones {
  local_phone: PacePhone | null;
  local_tty: PacePhone | null;
}

export interface PacePlan {
  contract_id: string;
  contract_year: string;
  office_hours: PaceOfficeHours[];
  organization_name: string;
  plan_id: string;
  plan_name_esp: string;
  plan_name: string;
  prospect_phones: PacePhones;
  segment_id: string;
  state: string;
  website: string;
}

export enum PaceTimeZone {
  TIME_ZONE_ALASKA = "TIME_ZONE_ALASKA",
  TIME_ZONE_CENTRAL = "TIME_ZONE_CENTRAL",
  TIME_ZONE_EASTERN = "TIME_ZONE_EASTERN",
  TIME_ZONE_HAWAII = "TIME_ZONE_HAWAII",
  TIME_ZONE_MOUNTAIN = "TIME_ZONE_MOUNTAIN",
  TIME_ZONE_PACIFIC = "TIME_ZONE_PACIFIC",
  TIME_ZONE_SAMOA = "TIME_ZONE_SAMOA",
}

export interface PapPlan {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone1: string;
  phone2: string;
  url: string;
  program_name: string;
  org_name: string;
  eligibility: string[];
  eligibility_spanish: string[];
}

export interface SpapPlan {
  phone1: string;
  program_name: string;
  state: string;
  url: string;
  restrictions: {
    income: boolean;
    age: boolean;
    asset: boolean;
    lis: boolean;
    part_d_enrollment: boolean;
    limited_pharmacies: boolean;
    other_factors: boolean;
    hiv_aids: boolean;
  };
}

export enum SupplementalBenefitCategoryTypeNames {
  SB_CAT_WORLDWIDE_EMERGENCY_URGENT_COVERAGE = "SB_CAT_WORLDWIDE_EMERGENCY_URGENT_COVERAGE",
  SB_CAT_CHIROPRACTIC_SERVICES = "SB_CAT_CHIROPRACTIC_SERVICES",
  SB_CAT_ADDITIONAL_TELEHEALTH_SERVICES = "SB_CAT_ADDITIONAL_TELEHEALTH_SERVICES",
  SB_CAT_OPIOID_TREATMENT_SERVICES = "SB_CAT_OPIOID_TREATMENT_SERVICES",
  SB_CAT_TRANSPORTATION_SERVICES = "SB_CAT_TRANSPORTATION_SERVICES",
  SB_CAT_OTC_ITEMS = "SB_CAT_OTC_ITEMS",
  SB_CAT_MEAL_BENEFIT = "SB_CAT_MEAL_BENEFIT",
  SB_CAT_ANNUAL_PHYSICAL_EXAM = "SB_CAT_ANNUAL_PHYSICAL_EXAM",
  SB_CAT_DEFINED_SUPPLEMENTAL_BENEFITS = "SB_CAT_DEFINED_SUPPLEMENTAL_BENEFITS",
  SB_CAT_REDUCED_COST_SHARING_FOR_VBID_UF_SSBCI = "SB_CAT_REDUCED_COST_SHARING_FOR_VBID_UF_SSBCI",
  SB_CAT_NON_PRIMARILY_HEALTH_RELATED_BENEFITS = "SB_CAT_NON_PRIMARILY_HEALTH_RELATED_BENEFITS",
  SB_CAT_HOME_HEALTH_SERVICES = "SB_CAT_HOME_HEALTH_SERVICES",
  SB_CAT_PROSTHETICS_MEDICAL_SUPPLIES = "SB_CAT_PROSTHETICS_MEDICAL_SUPPLIES",
  SB_CAT_ADDITIONAL_SERVICES = "SB_CAT_ADDITIONAL_SERVICES",
  SB_CAT_NON_OPIOID_PAIN_MANAGEMENT = "SB_CAT_NON_OPIOID_PAIN_MANAGEMENT",
  SB_CAT_DIALYSIS_SERVICES = "SB_CAT_DIALYSIS_SERVICES",
}

export type SupplementalBenefitType =
  | "SB_WORLDWIDE_EMERGENCY_TRANSPORTATION"
  | "SB_WORLDWIDE_EMERGENCY_COVERAGE"
  | "SB_WORLDWIDE_URGENT_COVERAGE"
  | "SB_OTHER"
  | "SB_ROUTINE_CARE"
  | "SB_MEDICARE_COVERED_VISITS"
  | "SB_MEDICARE_COVERED_BENEFITS"
  | "SB_PLAN_APPROVED_HEALTH_RELATED_LOCATION"
  | "SB_ANY_HEALTH_RELATED_LOCATION"
  | "SB_OTC_ITEMS"
  | "SB_MEAL_BENEFIT"
  | "SB_ANNUAL_PHYSICAL_EXAM"
  | "SB_PERSONAL_EMERGENCY_RESPONSE_SYSTEM_PERS"
  | "SB_IN_HOME_SUPPORT_SERVICES"
  | "SB_FITNESS_BENEFIT"
  | "SB_BATHROOM_SAFETY_DEVICES"
  | "SB_REDUCED_COST_SHARING_FOR_VBID_UF_SSBCI"
  | "SB_TRANSITIONAL_TEMPORARY_SUPPORTS"
  | "SB_FOOD_AND_PRODUCE"
  | "SB_MEALS_BEYOND_LIMITED_BASIS"
  | "SB_PEST_CONTROL"
  | "SB_TRANSPORTATION_FOR_NON_MEDICAL_NEEDS"
  | "SB_INDOOR_AIR_QUALITY_EQUIPMENT_AND_SERVICES"
  | "SB_SOCIAL_NEEDS_BENEFIT"
  | "SB_COMPLEMENTARY_THERAPIES"
  | "SB_SERVICES_SUPPORTING_SELF_DIRECTION"
  | "SB_STRUCTURAL_HOME_MODIFICATIONS"
  | "SB_ADDITIONAL_HOURS_OF_CARE"
  | "SB_HOME_PERSONAL_CARE_SERVICES"
  | "SB_OTHER1"
  | "SB_OTHER2"
  | "SB_NON_MEDICARE_COVERED_PROSTHETICS_MEDICAL_SUPPLIES"
  | "SB_EARLY_AND_PERIODIC_SCREENING"
  | "SB_PRIVATE_DUTY_NURSING_SERVICES"
  | "SB_CASE_MANAGEMENT_LONG_TERM_CARE"
  | "SB_INSTITUTION_FOR_MENTAL_DISEASE_SERVICES_FOR_65_OR_OLDER"
  | "SB_SERVICES_IN_AN_INTERMEDIATE_CARE_FACILITY_FOR_DISABILITIES"
  | "SB_CASE_MANAGEMENT"
  | "SB_TOBACCO_CESSATION_COUNSELING_FOR_PREGNANT_WOMEN"
  | "SB_FREESTANDING_BIRTH_CENTER_SERVICES"
  | "SB_RESPIRATORY_CARE_SERVICES"
  | "SB_FAMILY_PLANNING_SERVICES"
  | "SB_NURSING_HOME_SERVICES"
  | "SB_HOME_AND_COMMUNITY_BASED_SERVICES"
  | "SB_PERSONAL_CARE_SERVICES"
  | "SB_SELF_DIRECTED_PERSONAL_ASSISTANCE_SERVICES"
  | "SB_ALTERNATIVE_THERAPIES"
  | "SB_THERAPEUTIC_MASSAGE"
  | "SB_ACUPUNCTURE"
  | "SB_HEALTH_EDUCATION"
  | "SB_COUNSELING_SERVICES"
  | "SB_SUPPORT_FOR_CAREGIVERS_OF_ENROLLEES";

/**
 * BenefitData represents a benefit in plan_card.package_benefits
 */
export interface BenefitData {
  label: string;
  maBenefitService?: string;
  service: string;
}

export interface TypedBenefitData {
  label: string;
  maBenefitService?: PlanInfoBenefitService;
  service: PlanInfoBenefitService;
}

/**
 * MaBenefitData represents a benefit in plan_card.ma_benefits
 */
export interface MaBenefitData {
  label: string;
  category: PlanInfoBenefitCategory;
  service: PlanInfoBenefitService;
}

export interface PackageBenefit {
  readonly benefit_services: {
    readonly [x: string]: /* "SERVICE_PRIMARY_OR_SUCH" */ {
      readonly network_costs: NetworkCostDescriptors;
    };
  };
  readonly network_costs: NetworkCostDescriptors;
}

export interface PackageBenefits {
  readonly [x: string]: PackageBenefit;
}

export enum PackageServiceAvailability {
  ANY_DOCTOR = "AVAILABILITY_ANY_DOCTOR",
  ANY_WILLING_DOCTOR = "AVAILABILITY_ANY_WILLING_DOCTOR",
  INFORMATION_BEING_UPDATED = "AVAILABILITY_INFORMATION_BEING_UPDATED",
  INFORMATION_NOT_AVAILABLE = "AVAILABILITY_INFORMATION_NOT_AVAILABLE",
  NOT_PROVIDED = "AVAILABILITY_NOT_PROVIDED",
  NOT_SPECIFIED = "AVAILABILITY_NOT_SPECIFIED",
  PLAN_DOCTORS = "AVAILABILITY_PLAN_DOCTORS",
  PLAN_DOCTORS_MOST_SERVICES = "AVAILABILITY_PLAN_DOCTORS_MOST_SERVICES",
  PLAN_DOCTORS_SOME_SERVICES = "AVAILABILITY_PLAN_DOCTORS_SOME_SERVICES",
  PLAN_DOCTORS_WITH_EXCEPTIONS = "AVAILABILITY_PLAN_DOCTORS_WITH_EXCEPTIONS",
  PROVIDED = "AVAILABILITY_PROVIDED",
  PROVIDED_EXTRA_COST = "AVAILABILITY_PROVIDED_EXTRA_COST",
}

export interface PackageServices {
  readonly additional_physical_exams: PackageServiceAvailability;
  /**
   * @deprecated - now uses `ms_dental_services`
   */
  readonly dental_services: PackageServiceAvailability;
  readonly doctor_choice: PackageServiceAvailability;
  /**
   * @deprecated - now uses `ms_hearing_services`
   */
  readonly hearing_services: PackageServiceAvailability;
  readonly monthly_plan_premium?: PackageServiceAvailability;
  readonly outpatient_prescription: PackageServiceAvailability;
  readonly vision_services: PackageServiceAvailability;
  readonly ms_hearing_services: boolean;
  readonly ms_dental_services: boolean;
  readonly ms_vision_services: boolean;
}

export type BeneinfoClaimTypeCode =
  | "CLAIM_TYPE_CODE_UNKNOWN"
  | "CLAIM_TYPE_CODE_MEDICARE_PART_D_ORIGINAL_CLAIM"
  | "CLAIM_TYPE_CODE_MEDICARE_PART_D_ADJUSTED_CLAIM"
  | "CLAIM_TYPE_CODE_MEDICARE_PART_D_DELETED_CLAIM"
  | "CLAIM_TYPE_CODE_MEDICARE_PART_D_RESUBMITTED_CLAIM";

export type BeneinfoDrugCoverageStatusCode =
  | "DRUG_COVERAGE_STATUS_CODE_UNKNOWN"
  | "DRUG_COVERAGE_STATUS_CODE_COVERED"
  | "DRUG_COVERAGE_STATUS_CODE_SUPPLEMENTAL_DRUGS"
  | "DRUG_COVERAGE_STATUS_CODE_NON_COVERED_DRUGS"
  | "DRUG_COVERAGE_STATUS_CODE_OVER_THE_COUNTER_DRUGS";

export interface PDE {
  beneficiary_paid_usd: number;
  claim_date: DateString;
  claim_type_code: BeneinfoClaimTypeCode;
  drug: PrescriptionDrug;
  drug_coverage_status_code: BeneinfoDrugCoverageStatusCode;
  end_troop_total_usd: number;
  is_final_action: boolean;
  lics_liability_usd: number;
  pde_status: string;
  pharmacy: BeneinfoPharmacy;
  prescription_number: string;
  prescription_refill_number: number;
  third_party_paid_usd: number;
  troop_total_usd: number;
}

export type DrivingDistanceUnit = "miles" | "feet";

export interface DrivingDistanceInfo {
  driving_distance: number | null;
  driving_distance_units: DrivingDistanceUnit | null;
}
export interface BeneinfoPharmacy {
  city: string;
  name: string;
  npi: string;
  phone: string;
  state: string;
  street: string;
  unit: string;
  zipcode: string;
}
export interface Pharmacy extends DrivingDistanceInfo, BeneinfoPharmacy {
  distance_miles?: number;
  in_network?: boolean;
  preferred?: boolean;
}
export interface PharmacyResultsType {
  sourceAddress: PlanInfoAddressFeature;
  pharmacies: PharmacyGeoPoints;
  geoError: string;
  totalResults: number;
}
export interface PharmacyInfo extends DrivingDistanceInfo {
  npi: string;
  name: string;
  in_network: boolean;
  mail_order: boolean;
  preferred: boolean;
  ltc: boolean;
  formatted_yearly_drug_costs?: string;
}

export enum PharmacyFrequencies {
  days_30 = "days_30",
  days_90 = "days_90",
  days_any = "days_any",
}

export enum PharmacyPreferencesAndFrequencies {
  PHARM_PRFD_FREQ_30_DAYS = "PHARM_PRFD_FREQ_30_DAYS",
  PHARM_PRFD_FREQ_90_DAYS = "PHARM_PRFD_FREQ_90_DAYS",
  PHARM_STD_FREQ_30_DAYS = "PHARM_STD_FREQ_30_DAYS",
  PHARM_STD_FREQ_90_DAYS = "PHARM_STD_FREQ_90_DAYS",
  PHARM_MAIL_PRFD_FREQ_30_DAYS = "PHARM_MAIL_PRFD_FREQ_30_DAYS",
  PHARM_MAIL_PRFD_FREQ_90_DAYS = "PHARM_MAIL_PRFD_FREQ_90_DAYS",
  PHARM_MAIL_STD_FREQ_30_DAYS = "PHARM_MAIL_STD_FREQ_30_DAYS",
  PHARM_MAIL_STD_FREQ_90_DAYS = "PHARM_MAIL_STD_FREQ_90_DAYS",
}

export interface PharmacyGeoPointBase {
  type: "Feature";
  geometry: Point;
}

export interface PharmacyGeoPoint extends PharmacyGeoPointBase {
  /**
   * @deprecated
   * deprecated tag has been added to call out an inconsistency in our typing.
   * In the file FormattedResultsMessage, this should actually be typed as
   * `Pick<Pharmacy, "city" | "state" | "street" | "zipcode">`
   * Until this inconsistency has been addressed, use this with caution.
   * https://jira.cms.gov/browse/MCT-9957
   * */
  properties: Pharmacy;
}

export interface PlanInfoAddressFeature extends PharmacyGeoPointBase {
  /**
   * @deprecated - tag has been added to call out an inconsistency in our typing.
   *
   * According to Swagger, the `type` field here should be either `"Feature"` or
   * `"FeatureCollection"`, but this requires more investigation
   *
   * Also according to Swagger, this should actually be typed as
   * `Pick<Pharmacy, "city" | "state" | "street" | "zipcode">`
   * Until this inconsistency has been addressed, use this with caution.
   * https://jira.cms.gov/browse/MCT-9957
   * */
  properties: Partial<Pharmacy> &
    Pick<Pharmacy, "city" | "state" | "street" | "zipcode">;
}

export interface PharmacyGeoPoints {
  type: "FeatureCollection";
  features: PharmacyGeoPoint[];
}

export interface PlanInfoPointGeometry {
  coordinates: [number, number];
  description?: string;
  type: "Point";
}

export type PlanInfoGeoJsonType = "Feature" | "FeatureCollection";

export interface PharmaciesResponse {
  geocode_error: string;
  pharmacies: PharmacyGeoPoints;
  request_id: string;
  source_address: PlanInfoAddressFeature;
  source_location_type:
    | "coordinates"
    | "address"
    | "zipcode"
    | "complete_address";
  total_results: number;
}

export interface InNetworkPharmaciesResponse extends PharmaciesResponse {
  mail_order_network: boolean;
  mail_order_preferred: boolean;
}

export enum PharmacyType {
  Mail = "mail",
  MailAndRetail = "both",
  None = "",
  Retail = "retail",
}

export interface PhoneNumber {
  readonly extension: string;
  readonly number: string;
}

export interface PlanInfoCostShare {
  // values are from here:
  // https://github.com/CMSgov/oc-mct-api/blob/e2665f2b5ed812972316b0b43023aa6fe93acfff/planinfo/db/schema/101_schema.sql#L212-L215
  readonly network_status: "IN_NETWORK" | "OUT_OF_NETWORK" | "NO_NETWORK";
  readonly min_copay: number | null;
  readonly max_copay: number | null;
  readonly min_coinsurance: number | null;
  readonly max_coinsurance: number | null;
}

/* This enum's order controls the rendering order of cost-sharing data, so do not change*/
/* eslint-disable @typescript-eslint/naming-convention -- MCTAPI naming exception */
export enum IntervalType {
  INTERVAL_TYPE_NULL,
  INTERVAL_TYPE_DAY_INTERVAL,
  INTERVAL_TYPE_LIFETIME_DAY_INTERVAL,
  INTERVAL_TYPE_PER_STAY,
}
/* eslint-enable @typescript-eslint/naming-convention -- end MCTAPI naming exception*/

export interface PlanInfoTieredCostShare {
  /**
   * From the data team, according to a data-source review:
   *
   * - If `tier` is `null`, output should not reference a `tier`
   * - If `tier` is a `number`, output should reference the number as the `tier` value
   * - If there's only one `PlanInfoTieredCostShare` in the array and its `tier` is
   * `null`, there will be no output labelled with a `tier` value
   * - It's possible for the first `PlanInfoTieredCostShare` to have a `tier` of `null`
   * and then to have one or more other `PlanInfoTieredCostShare` objects with (a)
   * `tier`(s) number(s)
   */
  tier: number | null;
  interval_type: keyof typeof IntervalType;
  interval: `${number}-${number}` | null;
  copay: number | null;
  coinsurance: number | null;
}

/**
 * From the data team, according to a data-source review:
 *
 * If `network_status` is `no_network`, there should be no more `network_status` information
 */
export interface PlanInfoTieredCostSharing {
  no_network: PlanInfoTieredCostShare[];
  in_network: PlanInfoTieredCostShare[];
  out_of_network: PlanInfoTieredCostShare[];
}

/**
 * plan benefit categories
 * This enum is incomplete, and is currently only defining
 * the hearing and dental categories that we are using on the frontend.
 * */
export enum PlanBenefitCategory {
  HearingAids = "HEARING_AIDS",
  HearingExams = "HEARING_EXAMS",
  DentalPreventive = "BENEFIT_PREVENTIVE_DENTAL",
  DentalComprehensive = "BENEFIT_COMPREHENSIVE_DENTAL",
}
type PlanBenefitCategoryString =
  `${(typeof PlanBenefitCategory)[keyof typeof PlanBenefitCategory]}`;

/** `service` options for category "HEARING_AIDS", from `plan_card.ma_benefits` */
export enum HearingAidsService {
  All = "ALL_HEARING_AIDS",
  Rx = "RX_HEARING_AIDS",
  Otc = "OTC_HEARING_AIDS",
}
type HearingAidsServiceString =
  `${(typeof HearingAidsService)[keyof typeof HearingAidsService]}`;

/** `service` options for category "HEARING_EXAM" from `plan_card.ma_benefits` */
export enum HearingExamService {
  FittingEvaluation = "FITTING_EVALUATION_HEARING_AIDS",
  Routine = "ROUTINE_HEARING_EXAMS",
}
type HearingExamServiceString =
  `${(typeof HearingExamService)[keyof typeof HearingExamService]}`;

/** `service` options for category "BENEFIT_PREVENTIVE_DENTAL" from `plan_card.ma_benefits` */
export enum DentalPreventiveService {
  OralExam = "SERVICE_ORAL_EXAM",
  Cleaning = "PROPHYLAXIS",
  FluorideTreatment = "SERVICE_FLUORIDE_TREATMENT",
  Xrays = "SERVICE_DENTAL_XRAYS",
}
type DentalPreventiveServiceString =
  `${(typeof DentalPreventiveService)[keyof typeof DentalPreventiveService]}`;

/** `service` options for category "BENEFIT_COMPREHENSIVE_DENTAL" from `plan_card.ma_benefits` */
export enum DentalComprehensiveService {
  RestorativeServices = "SERVICE_RESTORATIVE_SERVICES",
  Endodontics = "SERVICE_ENDODONTICS",
  Periodontics = "SERVICE_PERIODONTICS",
  ProsthodonticsRemovable = "PROSTHODONTICS_REMOVABLE",
  ProsthodonticsFixed = "PROSTHODONTICS_FIXED",
  MaxillofacialProsthetics = "MAXILLOFACIAL_PROSTHETICS",
  ImplantServices = "IMPLANT_SERVICES",
  OralMaxillofacialSurgery = "ORAL_AND_MAXILLOFACIAL_SURGERY",
  Orthodontics = "ORTHODONTICS",
  AdjunctiveGeneralServices = "ADJUNCTIVE_GENERAL_SERVICES",
}
type DentalComprehensiveServiceString =
  `${(typeof DentalComprehensiveService)[keyof typeof DentalComprehensiveService]}`;

export enum PlanInfoBenefitType {
  MedicareCovered = "MEDICARE_COVERED",
  OptionalSupplemental = "OPTIONAL_SUPPLEMENTAL",
  MandatorySupplemental = "MANDATORY_SUPPLEMENTAL",
}

export type PlanLimitDetailBenefitLimitType =
  | "BENEFIT_LIMIT_TYPE_NULL"
  | "BENEFIT_LIMIT_TYPE_VISITS"
  | "BENEFIT_LIMIT_TYPE_OOP"
  | "BENEFIT_LIMIT_TYPE_COVERAGE"
  | "BENEFIT_LIMIT_TYPE_PER_EAR"
  | "BENEFIT_LIMIT_TYPE_BOTH_EARS"
  | "BENEFIT_LIMIT_TYPE_INN_COVERAGE"
  | "BENEFIT_LIMIT_TYPE_COMBINED_COVERAGE"
  | "BENEFIT_LIMIT_TYPE_TRIPS"
  | "BENEFIT_LIMIT_TYPE_QUANTITY"
  | "BENEFIT_LIMIT_TYPE_OON_COVERAGE";

export type PlanLimitDetailBenefitLimitPeriod =
  | "BENEFIT_LIMIT_PERIOD_NULL"
  | "BENEFIT_LIMIT_PERIOD_EVERY_THREE_YEARS"
  | "BENEFIT_LIMIT_PERIOD_EVERY_TWO_YEARS"
  | "BENEFIT_LIMIT_PERIOD_EVERY_YEAR"
  | "BENEFIT_LIMIT_PERIOD_EVERY_SIX_MONTHS"
  | "BENEFIT_LIMIT_PERIOD_EVERY_THREE_MONTHS"
  | "BENEFIT_LIMIT_PERIOD_OTHER_DESCRIBE"
  | "BENEFIT_LIMIT_PERIOD_EVERY_MONTH"
  | "BENEFIT_LIMIT_PERIOD_NOT_DEFINED";

export interface PlanInfoPlanLimitDetail {
  readonly limit_type: PlanLimitDetailBenefitLimitType;
  readonly limit_value: number;
  readonly limit_period: PlanLimitDetailBenefitLimitPeriod;
}

/**
 * Every possible benefit category name from MCTAPI
 * @see `PlanInfoBenefitCategory` to confirm with Swagger or MCTAPI code
 */
export type PlanInfoBenefitCategory =
  | PlanBenefitCategoryString
  | "BENEFIT_NOT_SPECIFIED"
  | "BENEFIT_AMBULANCE"
  | "BENEFIT_DIAGNOSTIC_PROCEDURES"
  | "BENEFIT_DOCTOR_VISITS"
  | "BENEFIT_MEDICAL_EQUIPMENT"
  | "BENEFIT_EMERGENCY_CARE"
  | "BENEFIT_FOOT_CARE"
  | "BENEFIT_HEARING"
  | "BENEFIT_MENTAL_HEALTH"
  | "BENEFIT_REHABILITATION_SERVICES"
  | "BENEFIT_OUTPATIENT_HOSPITAL"
  | "BENEFIT_TRANSPORTATION"
  | "BENEFIT_VISION"
  | "BENEFIT_PREVENTIVE_CARE"
  | "BENEFIT_INPATIENT_HOSPITAL"
  | "BENEFIT_SKILLED_NURSING_FACILITY"
  | "BENEFIT_OUTPATIENT_PRESCRIPTION_DRUGS"
  | "BENEFIT_OPTIONAL_BENEFITS"
  | "BENEFIT_WELLNESS_PROGRAMS"
  | "BENEFIT_MEDICARE_PART_B"
  | "BENEFIT_PACKAGE_1"
  | "BENEFIT_PACKAGE_2"
  | "BENEFIT_PACKAGE_3"
  | "BENEFIT_PACKAGE_4"
  | "BENEFIT_PACKAGE_5"
  | "BENEFIT_PACKAGE_6"
  | "BENEFIT_PACKAGE_7"
  | "BENEFIT_PACKAGE_8"
  | "BENEFIT_PACKAGE_9"
  | "BENEFIT_OTHER_DEDUCTIBLES"
  | "BENEFIT_MAXIMUM_OOPC"
  | "BENEFIT_OPTIONAL_SUPPLEMENTAL_BENEFITS"
  | "BENEFIT_HEALTH_PLAN_DEDUCTIBLE"
  | "OTHER_SERVICES"
  | "BENEFIT_MEDICARE_COVERED_ZERO_COSTSHARING_PREVENTIVE_SERVICES"
  | "PART_B_DRUGS"
  | "PROFESSIONAL_SERVICES"
  | "PREVENTIVE_SERVICES"
  | "BENEFITS_CHRONICALLY_ILL";

/**
 * Every possible benefit service name from MCTAPI
 * @see `PlanInfoBenefitService` to confirm with Swagger or MCTAPI code
 */
export type PlanInfoBenefitService =
  | DentalPreventiveServiceString
  | DentalComprehensiveServiceString
  | HearingExamServiceString
  | HearingAidsServiceString
  | "SERVICE_NOT_SPECIFIED"
  | "SERVICE_PRIMARY"
  | "SERVICE_SPECIALIST"
  | "SERVICE_EMERGENCY"
  | "SERVICE_URGENT_CARE"
  | "SERVICE_DIAGNOSTIC_TESTS"
  | "SERVICE_LAB_SERVICES"
  | "SERVICE_DIAGNOSTIC_RADIOLOGY_SERVICES"
  | "SERVICE_OUTPATIENT_XRAYS"
  | "SERVICE_HEARING_EXAM"
  | "SERVICE_FITTING_EVALUATION"
  | "SERVICE_HEARING_AIDS"
  | "SERVICE_HEARING_AIDS_INNER_EAR"
  | "SERVICE_HEARING_AIDS_OUTER_EAR"
  | "SERVICE_HEARING_AIDS_OVER_THE_EAR"
  | "SERVICE_OFFICE_VISIT"
  | "SERVICE_CLEANING"
  | "SERVICE_MEDICARE_COVERED_COMPREHENSIVE_DENTAL_SERVICES"
  | "SERVICE_NONROUTINE_SERVICES"
  | "SERVICE_DIAGNOSTIC_SERVICES"
  | "SERVICE_EXTRACTIONS"
  | "SERVICE_PROSTHODONTICS_AND_OTHER_SERVICES"
  | "SERVICE_MEDICARE_COVERED_EYE_EXAM"
  | "SERVICE_ROUTINE_EYE_EXAM"
  | "SERVICE_OTHER"
  | "SERVICE_MEDICARE_COVERED_EYEWEAR"
  | "SERVICE_CONTACT_LENSES"
  | "SERVICE_EYEGLASSES_FRAMES_AND_LENSES"
  | "SERVICE_EYEGLASS_FRAMES"
  | "SERVICE_EYEGLASS_LENSES"
  | "SERVICE_UPGRADES"
  | "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT_WITH_PSYCHIATRIST"
  | "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT_WITH_PSYCHIATRIST"
  | "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT"
  | "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT"
  | "SERVICE_OCCUPATIONAL_THERAPY_VISIT"
  | "SERVICE_PHYSICAL_THERAPY_AND_SPEECH_AND_LANGUAGE_THERAPY_VISIT"
  | "SERVICE_FOOT_EXAMS_AND_TREATMENT"
  | "SERVICE_ROUTINE_FOOT_CARE"
  | "SERVICE_DURABLE_MEDICAL_EQUIPMENT"
  | "SERVICE_PROSTHETICS"
  | "SERVICE_DIABETES_SUPPLIES"
  | "SERVICE_CHEMOTHERAPY"
  | "SERVICE_OTHER_PART_B_DRUGS"
  | "SERVICE_MONTHLY_PREMIUM"
  | "SERVICE_DEDUCTIBLE"
  | "SERVICE_COST_PLAN_CHEMOTHERAPY"
  | "SERVICE_COST_PLAN_OTHER_PART_B_DRUGS"
  | "SERVICE_PART_B_INSULIN"
  | "MEDICARE_COVERED_BENEFITS"
  | "OVER_EAR_HEARING_AIDS_SINGLE"
  | "OVER_EAR_HEARING_AIDS_BOTH"
  | "OUTER_EAR_HEARING_AIDS_SINGLE"
  | "OUTER_EAR_HEARING_AIDS_BOTH"
  | "INNER_EAR_HEARING_AIDS_SINGLE"
  | "INNER_EAR_HEARING_AIDS_BOTH"
  | "OTHER_DIAGNOSTIC_SERVICES"
  | "OTHER_PREVENTATIVE_DENTAL_SERVICES"
  | "VISION_ROUTINE_EYE_EXAMS"
  | "VISION_CONTACT_LENSES"
  | "VISION_EYEGLASSES_FRAMES"
  | "VISION_EYEGLASSES_LENSES"
  | "VISION_EYEGLASSES_FRAMES_AND_LENSES"
  | "VISION_UPGRADES"
  | "SERVICE_OPIOID_TREATMENT_PROGRAM_SERVICES"
  | "SERVICE_OUTPATIENT_HOSPITAL_SERVICES"
  | "GROUND_AMBULANCE"
  | "PART_B_CHEMOTHERAPY_DRUGS"
  | "PART_B_OTHER_DRUGS"
  | "SERVICE_DIALYSIS"
  | "SERVICE_MEDICARE_COVERED_ZERO_DOLLAR_PREVENTIVE_SERVICES"
  | "SKILLED_NURSING_FACILITY"
  | "INPATIENT_HOSPITAL"
  | "CHIROPRACTIC"
  | "TELEHEALTH"
  | "NON_EMERGENCY_CARE_PLAN_APPROVED"
  | "NON_EMERGENCY_CARE_ANY_HEALTH_LOCATION"
  | "ACUPUNCTURE"
  | "OTC_ITEMS"
  | "MEALS_SHORT_DURATION"
  | "ANNUAL_PHYSICAL"
  | "HEALTH_EDUCATION"
  | "FITNESS"
  | "HOME_BATHROOM_SAFETY"
  | "COUNSELING"
  | "PERS"
  | "POST_DISCHARGE_MED_REC"
  | "MASSAGE"
  | "ALT_THERAPIES"
  | "READMISSION_PREVENTION"
  | "WIGS_CHEMO_RELATED"
  | "WEIGHT_MANAGEMENT"
  | "ADULT_DAY_HEALTH"
  | "HOME_PALLIATIVE_CARE"
  | "IN_HOME_SUPPORT"
  | "CAREGIVER_SUPPORT"
  | "SERVICE_WORLDWIDE_EMERGENCY"
  | "FOOD_PRODUCE"
  | "MEALS"
  | "PEST_CONTROL"
  | "TRANSPORTATION_NON_MED_ANY"
  | "TRANSPORTATION_NON_MED_PLAN_APPROVED"
  | "AIR_QUALITY_EQUIPMENT"
  | "SOCIAL_NEEDS"
  | "COMPLEMENTARY_THERAPIES"
  | "SUPPORT_SELF_DIRECTION"
  | "STRUCTURAL_HOME_MODS"
  | "GENERAL_SUPPORTS"
  | "NUTRITIONAL_DIETARY_BENEFIT"
  | "ADDL_SMOKING_AND_TOBACCO_CESSATION_COUNSELING"
  | "ENHANCED_DISEASE_MANAGEMENT"
  | "TELEMONITORING_SERVICES"
  | "REMOTE_ACCESS_TECHNOLOGIES_WEB_PHONE_BASED"
  | "REMOTE_ACCESS_TECHNOLOGIES_NURSING_HOTLINE"
  | "IN_HOME_SAFETY_ASSESSMENT"
  | "MEDICAL_NUTRITION_THERAPY_MNT";

/**
 * `ma_benefits` on the Plan object
 * see definition here https://test.medicare.gov/plan-compare/docs/#model-planinfoBenefit
 */
export interface PlanInfoBenefit {
  readonly category: PlanInfoBenefitCategory;
  readonly type: PlanInfoBenefitType;
  readonly authorization_required: boolean;
  readonly referral_required: boolean;
  readonly service: PlanInfoBenefitService;
  readonly cost_sharing: PlanInfoCostShare[];
  readonly plan_limits: boolean;
  readonly plan_limits_details: PlanInfoPlanLimitDetail[];
  readonly tiered_cost_sharing: PlanInfoTieredCostSharing | null;
}

export interface Plan extends BasePlan {
  readonly abstract_benefits?: AbstractBenefits;
  readonly additional_supplemental_benefits: AdditionalSupplementalBenefits | null;
  readonly address_1: string;
  readonly address_2: string;
  readonly city: string;
  readonly member_phone: PhoneNumber | null;
  readonly optional_benefits: OptionalBenefitPackage[];
  readonly package_benefits: PackageBenefits;
  readonly prospect_phone: PhoneNumber | null;
  readonly star_ratings: StarRating[];
  readonly state: string;
  readonly zipcode: string;
  readonly provider_network_web_address: string;
  readonly non_zero_dollar_dsnp: boolean;
  // Medicare Advantage Benefits
  readonly ma_benefits: PlanInfoBenefit[];
}

export interface PlanResponse {
  request_id: string;
  plan_card: Plan;
}

export interface PlanCardResponse {
  request_id: string;
  plan: SearchResultPlan;
}

export enum PlanCategory {
  ChronicCare = "PLAN_CATEGORY_CHRONIC_CARE",
  Cost1876 = "PLAN_CATEGORY_1876_COST",
  DemonstrationSnp = "PLAN_CATEGORY_DEMONSTRATION_SNP",
  EmployerPdp = "PLAN_CATEGORY_EMPLOYER_PDP",
  EmployerPffs = "PLAN_CATEGORY_EMPLOYER_PFFS",
  EmployerPpo = "PLAN_CATEGORY_EMPLOYER_PPO",
  Fallback = "PLAN_CATEGORY_FALLBACK",
  Hcpp1833Cost = "PLAN_CATEGORY_HCPP_1833_COST",
  Hmo = "PLAN_CATEGORY_HMO",
  HmoPos = "PLAN_CATEGORY_HMOPOS",
  LocalPpo = "PLAN_CATEGORY_LOCAL_PPO",
  MedicarePdp = "PLAN_CATEGORY_MEDICARE_PDP",
  Msa = "PLAN_CATEGORY_MSA",
  Na = "PLAN_CATEGORY_NA",
  NationalPace = "PLAN_CATEGORY_NATIONAL_PACE",
  OriginalMedicare = "PLAN_CATEGORY_ORIGINAL_MEDICARE",
  Pffs = "PLAN_CATEGORY_PFFS",
  PosContractor = "PLAN_CATEGORY_POS_CONTRACTOR",
  Pso = "PLAN_CATEGORY_PSO",
  RegionalPpo = "PLAN_CATEGORY_REGIONAL_PPO",
  RfbHmo = "PLAN_CATEGORY_RFB_HMO",
  RfbHmoPos = "PLAN_CATEGORY_RFB_HMOPOS",
  RfbLocalPpo = "PLAN_CATEGORY_RFB_LOCAL_PPO",
  RfbPffs = "PLAN_CATEGORY_RFB_PFFS",
  RfbPso = "PLAN_CATEGORY_RFB_PSO",
  Unknown = "PLAN_CATEGORY_UNKNOWN",
}

export interface PlanDataVersion {
  file: string;
  sha1_hash: string;
  file_date: string;
  metadata: string;
}

export enum PlanMaterialLanguage {
  DEFAULT = "",
  SPANISH = "Spanish",
  OTHER = "Other",
}

export type PlanOrSRP = Plan | SearchResultPlan;

export interface PlanResults {
  readonly plans: SearchResultPlan[];
  readonly request_id: string;
  readonly search_summary: PlanResultsSearchSummary;
  readonly total_results: number;
}

export interface PlanResultsSearchSummary {
  categories: PlanCategoryFilters;
  category_names?: PlanCategory[];
  dental: number;
  drugs: number;
  excluded: number;
  hearing: number;
  national: number;
  no_drugs: number;
  organizations: {
    count: number;
    name: string;
  }[];
  organization_names: string[];
  overall_star_ratings: {
    // TODO [2026-08-01]: These can be changed from optional to required once the backend is providing these values.
    zero?: number;
    one?: number;
    two?: number;
    three?: number;
    four?: number;
    zero_and_up: number;
    one_and_up: number;
    two_and_up: number;
    three_and_up: number;
    four_and_up: number;
    five: number;
  };
  sanctioned: number;
  senior_savings_model: number;
  transportation: number;
  silver_sneakers: number;
  vision: number;
}

export interface PlanCategoryFilters {
  cost: number;
  hmo: number;
  mmp: number;
  msa: number;
  pffs: number;
  ppo: number;
  /** special needs plan */
  snp: number;
  /** chronic condition */
  c_snp: number;
  /** dual eligible */
  d_snp: number;
  /** institutional */
  i_snp: number;
}

export interface PlanSearch {
  [s: string]: unknown;
  readonly fips?: string;
  readonly title?: string;
  readonly year: string;
}

/** SNP = Special Needs Plan */
export enum PlanSnpType {
  /** Have chronic or disabling condition (like diabetes, heart disease, cancer, dementia, or stroke). */
  ChronicOrDisabling = "SNP_TYPE_CHRONIC_OR_DISABLING",
  /** Have both Medicare and Medicaid. */
  DualEligible = "SNP_TYPE_DUAL_ELIGIBLE",
  /** Need long-term care services, either in a facility or at home. */
  Institutional = "SNP_TYPE_INSTITUTIONAL",
  NotSnp = "SNP_TYPE_NOT_SNP",
}

/** a list of SNP types that only comprises ones a user can select to filter on */
export type SnpFilter =
  | PlanSnpType.ChronicOrDisabling
  | PlanSnpType.DualEligible
  | PlanSnpType.Institutional;

export enum PlanType {
  /** Medicare Advantage */
  MA = "PLAN_TYPE_MA",
  /** Medicare Advantage (MA) Plan that offers Medicare Part D (Prescription Drug Coverage) */
  MAPD = "PLAN_TYPE_MAPD",
  /** Medigap */
  MEDIGAP = "PLAN_TYPE_MEDIGAP",
  /** Prescription Drug Coverage */
  PDP = "PLAN_TYPE_PDP",
}

export enum PlanDataStatus {
  None = "none",
  Loading = "loading",
  Success = "success",
  Error = "error",
}

export interface PlanCoverageProviderLocation {
  address: string;
  covered: boolean;
  id: string;
}

export interface PlanInfoProviderCoverageProvider {
  npi: string;
  covered: boolean;
  locations: PlanCoverageProviderLocation[];
}

export interface PlanInfoProviderCoverage {
  has_provider_coverage_data: boolean;
  providers: PlanInfoProviderCoverageProvider[];
}

export interface PrescriptionDrug {
  drug_pap_id: string;
  form: string;
  frequency: PrescriptionFrequency;
  insulin: boolean;
  is_generic: boolean;
  name: string;
  ndc: string;
  package_quantity: string | number;
  package_size: string | number;
  package_type: string;
  package_units: string;
  quantity: number | string;
  rxcui: string;
  strength_units: string;
  strength: string;
  use_package_quantity: boolean;
  vaccine: boolean;
}

export enum PrescriptionFrequency {
  DAYS_30 = "FREQUENCY_30_DAYS",
  DAYS_60 = "FREQUENCY_60_DAYS",
  DAYS_90 = "FREQUENCY_90_DAYS",
  DAYS_180 = "FREQUENCY_180_DAYS",
  DAYS_360 = "FREQUENCY_360_DAYS",
}

export interface DrugCostPrescriptionInfo {
  ndc: string;
  quantity: string;
  frequency: PrescriptionFrequency;
}

export enum DrugPricingRedactionIssue {
  formulary = "formulary",
  formulary_review = "frmlry_revw",
  partd = "partd",
  pharmacy = "pharmacy",
}

export enum PlanBenefitRedactionIssue {
  bid = "bid",
  contract = "contract",
  "ma-network" = "ma-network",
  pln_corrctn = "pln_corrctn",
  other = "other",
}

export enum OtherRedactionIssue {
  enr_sanction = "enr_sanction",
  frzn_enrlmnt = "frzn_enrlmnt",
  cntrct_trmtn = "cntrct_trmtn",
  d_snp = "d_snp",
}

export enum RedactionType {
  suppression = "suppression",
  exclusion = "exclusion",
  frozen = "frozen",
  sanction = "sanction",
}

export interface Redaction {
  readonly issue:
    | keyof typeof OtherRedactionIssue
    | keyof typeof PlanBenefitRedactionIssue
    | `${DrugPricingRedactionIssue}`;
  readonly type: keyof typeof RedactionType;
  readonly url: string;
}

export interface SearchResultPlan extends BasePlan {
  readonly annual_drugs_total: number;
  readonly annual_drugs_total_retail: number;
  readonly annual_drugs_total_mail_order: number;
  readonly counseling_services: boolean;
  readonly covered_drugs: number;
  readonly does_not_support_mail_order: boolean;
  readonly emergency_care_cost: string;
  readonly emergency_response_device: boolean;
  readonly health_education: boolean;
  readonly home_safety_devices: boolean;
  readonly in_home_support: boolean;
  readonly maximum_oopc: string;
  readonly otc_drugs: boolean;
  readonly pharmacies: SearchResultPlanPharmacy[];
  readonly primary_doctor_visit_cost: string;
  readonly remaining_premium_and_drugs: number;
  readonly specialist_doctor_visit_cost: string;
  readonly support_for_caregivers: boolean;
  readonly telehealth: boolean;
  readonly remaining_premium_and_drugs_mail_order: number;
  readonly remaining_premium_and_drugs_retail: number;
  readonly transportation: boolean;
  readonly worldwide_emergency: boolean;
  readonly primary_doctor_cost_sharing: PlanInfoCostShare | null;
  readonly specialist_doctor_cost_sharing: PlanInfoCostShare | null;
  readonly non_zero_dollar_dsnp: boolean;
}

export interface SearchResultPlanPharmacy {
  readonly in_network: boolean;
  readonly npi: string;
}

export enum PlanCategoryFilter {
  HMO = "PLAN_FILTER_HMO",
  PPO = "PLAN_FILTER_PPO",
  PFFS = "PLAN_FILTER_PFFS",
  MSA = "PLAN_FILTER_MSA",
  Cost = "PLAN_FILTER_COST",
}

export interface SearchResultsFilters {
  provider: string;
  drugCoveragesFilter: DrugCoverageFilterOption[];
  insuranceCarriersFilter: string[];
  /** @deprecated -- no longer needed with `tmp-enable-new-category-org-filters` flag */
  insuranceCarriers: string[];
  planCategoriesFilter: PlanCategoryFilter[];
  planCoveragesFilter: string[];
  sortBy?: SortOptions;
  starRatingsFilter: StarRatingOption[];
  /** is the "Include SNP" switch toggled to on? */
  includeSnp: boolean;
}

export type StarRatingOption = "0" | "1" | "2" | "3" | "4" | "5";

export enum Sex {
  FEMALE = "female",
  MALE = "male",
  NONE = "",
}

export enum SortOptions {
  ANNUAL_DEDUCTIBLE = "ANNUAL_DEDUCTIBLE",
  ANNUAL_DRUGS_TOTAL = "ANNUAL_DRUGS_TOTAL",
  ANNUAL_TOTAL = "ANNUAL_TOTAL",
  DRUG_PLAN_DEDUCTIBLE = "DRUG_PLAN_DEDUCTIBLE",
  MONTHLY_PREMIUM = "MONTHLY_PREMIUM",
  /**
   * NOT used for sorting (obviously). Passed to `PlanCard` from `PlanPreviewPage`,
   * which displays a single card above plan details, and therefore has no
   * sort order
   */
  NONE = "NONE",
}

export type DefaultSortOption =
  | SortOptions.ANNUAL_TOTAL
  | SortOptions.MONTHLY_PREMIUM;

export interface OriginalMedicareStarRatings {
  star_ratings: StarRating[];
}

export interface StarRating {
  category: string;
  error: StarRatingError;
  rating: number;
}

export enum StarRatingError {
  STAR_ERROR_NO_DATA = "STAR_ERROR_NO_DATA",
  STAR_ERROR_NO_ERROR = "STAR_ERROR_NO_ERROR",
  STAR_ERROR_NOT_ENOUGH_DATA = "STAR_ERROR_NOT_ENOUGH_DATA",
  STAR_ERROR_NOT_OFFERED = "STAR_ERROR_NOT_OFFERED",
  STAR_ERROR_NOT_REQUIRED = "STAR_ERROR_NOT_REQUIRED",
  STAR_ERROR_PLAN_TOO_NEW = "STAR_ERROR_PLAN_TOO_NEW",
  STAR_ERROR_PLAN_TOO_SMALL = "STAR_ERROR_PLAN_TOO_SMALL",
  STAR_ERROR_NOT_REQUIRED_TO_REPORT = "STAR_ERROR_NOT_REQUIRED_TO_REPORT",
}

export interface StringMap {
  [s: string]: string;
}

export enum Title {
  MISS = "Miss",
  MR = "Mr.",
  MRS = "Mrs.",
  MS = "Ms.",
  NONE = "",
}

export enum GetBenefitsFrom {
  WITHHOLD_NONE = "WITHHOLD_NONE",
  RRB = "WITHHOLD_RRB",
  SSI = "WITHHOLD_SSI",
}

export enum UserLanguage {
  English = "en",
  Spanish = "es",
}

export enum QueryStringLanguage {
  ENGLISH = "en-us",
  SPANISH = "es-mx",
}

export interface UtagModule {
  link: (obj: UtagEvent) => void;
}

export interface UtagEvent {
  event_action: string;
  event_category: "MCT";
  event_label: string;
}

export interface Window {
  PHRASEAPP_ENABLED: boolean;
}

export interface LocationWithNextRouteInState {
  nextRoute?: H.LocationDescriptorObject;
}

export type LocationWithOnlyPathnameRequired = Partial<
  RouteComponentProps["location"]
> &
  Pick<RouteComponentProps["location"], "pathname">;

export type LocationWithOptionalState = Omit<
  RouteComponentProps["location"],
  "state"
> &
  Partial<Pick<RouteComponentProps["location"], "state">>;

export enum MBPTargets {
  ENROLL_STATUS = "ENROLL_STATUS",
  GATEWAY = "GATEWAY",
  PLAN = "PLAN",
  PRESCRIPTIONS = "PRESCRIPTIONS",
  STAR_RATINGS = "STAR_RATINGS",
  PHARMACIES = "PHARMACIES",
}

export interface MBPLandingParams {
  code?: string;
  req_token?: string;
  target?: string;
  planYear?: string;
  contractId?: string;
  planId?: string;
  segmentId?: string;
  fips?: string;
  plan_type?: string;
  zipCode?: string | number;
  lang?: string;
  relay?: string;
  mbp_redirect?: string;
}

export enum NewRelicPageActionNames {
  LOGOUT = "Logout",
  SEARCH_RESULTS_FILTER = "Search Results Filter",
}

export interface NewRelicPageActionLogoutValues extends Record<
  string,
  newrelic.SimpleType
> {
  activityId: string;
  beneficiaryKey: string;
  csrId: string;
  currentTimeUtc: string;
  forceLogoutModalShown: string;
  forceLogoutTimeoutMs: string;
  hasSessionAndBene: string;
  slsSessionExpired: string;
  lastActivityUtc: string;
}

export interface NewRelicPageActionSearchResultsFilterValues extends Record<
  string,
  newrelic.SimpleType
> {
  filter: string;
  value: string;
}

export interface NewRelicPageActionValuesMap extends Record<
  NewRelicPageActionNames,
  Record<string, newrelic.SimpleType>
> {
  [NewRelicPageActionNames.LOGOUT]: NewRelicPageActionLogoutValues;
  [NewRelicPageActionNames.SEARCH_RESULTS_FILTER]: NewRelicPageActionSearchResultsFilterValues;
}

export interface NewRelicPageActionInterface<T> {
  addNewRelicPageAction<K extends keyof T>(key: K, value: T[K]): void;
}

export interface PlanID {
  contract_id: string;
  plan_id: string;
  segment_id: string;
  contract_year: string;
}

export interface PlanPreferredPharmacyInfo {
  plan: PlanID;
  retail_preferred: number;
  mail_preferred: number;
}

// Initial Enrollment Period Status
export enum IEPStatus {
  /**
   * 65 - 4 month
   */
  IEP_STATUS_BEFORE_IEP = "IEP_STATUS_BEFORE_IEP",
  /**
   * 65 +- 3 month
   */
  IEP_STATUS_WITHIN_IEP = "IEP_STATUS_WITHIN_IEP",
  /**
   * 65 + 4 month
   */
  IEP_STATUS_PAST_IEP = "IEP_STATUS_PAST_IEP",
}

export enum NewToMedicareResult {
  /**
   * A. Ready to join plans
   */
  NEW_TO_MEDICARE_RESULT_READY_TO_JOIN = "A_READY_TO_JOIN",
  /**
   * B. Join plan within 63 days of retiring
   */
  NEW_TO_MEDICARE_RESULT_JOIN_WITHIN_DAYS_OF_RETIRING = "B_JOIN_WITHIN_DAYS_OF_RETIRING",
  /**
   * C. You may have a penalty
   */
  NEW_TO_MEDICARE_RESULT_MAY_HAVE_PENALTY = "C_MAY_HAVE_PENALTY",
  /**
   * D. Join plan during disability IEP
   */
  NEW_TO_MEDICARE_RESULT_JOIN_DURING_DISABILITY_IEP = "D_JOIN_DURING_DISABILITY_IEP",
  /**
   * E. Wait until you are 65
   */
  NEW_TO_MEDICARE_RESULT_WAIT_UNTIL_65 = "E_WAIT_UNTIL_65",
  /**
   * F. Late. Get A/B and come back to join plan ASAP
   */
  NEW_TO_MEDICARE_RESULT_LATE = "F_LATE",
  /**
   * G. Go get A/B now and mind your IEP and SEP
   */
  NEW_TO_MEDICARE_RESULT_GET_AB = "G_GET_AB",
}

export interface NewToMedicareAlertInfo {
  /** from wizard or bene.birthdate */
  birthdate: Date | undefined;
  /** from wizard or bene.medicare_info */
  iepStatus: IEPStatus | undefined;
  /** from wizard
   * @warning this currently only applies to anonymous benes.
   * */
  newToMedicareResult: NewToMedicareResult | undefined;
  /** from bene.coverage_current or bene.coverage_future */
  hasMedigap: boolean;
  /** from bene.coverage_current or bene.coverage_future */
  hasPDPPlan: boolean;
  /** from bene.coverage_current or bene.coverage_future */
  hasMAPDPlan: boolean;
  /** from bene.medicare_info */
  hasPartAandB: boolean;
}

export interface WizardAnswer {
  hasMedicareNumber?: boolean;
  dob?: MCTDateProp;
  validDob?: boolean;
  iepStartDate?: Date;
  iepEndDate?: Date;
  iepStatus?: IEPStatus;
  olderThan65?: boolean;
  hasCreditableCoverage?: boolean;
  coverageEndingSoon?: boolean;
  hasDisability?: boolean;
}

/** ----- Other Insurances / Medigap ----- */

/** beneinfo/otherinsurance `coba_type` parameter */
export enum CobaTypeFilter {
  All = "COBA_TYPE_FILTER_ALL",
  Supplemental = "COBA_TYPE_FILTER_SUPPLEMENTAL",
  Medigap = "COBA_TYPE_FILTER_MEDIGAP",
  Tricare = "COBA_TYPE_FILTER_TRICARE",
  Medicaid = "COBA_TYPE_FILTER_MEDICAID",
  ChampVa = "COBA_TYPE_FILTER_CHAMPVA",
}

/** beneinfo/otherinsurance `status` parameter */
export enum OtherInsuranceStatus {
  All = "OTHER_INSURANCE_STATUS_ALL",
  Future = "OTHER_INSURANCE_STATUS_FUTURE",
  Current = "OTHER_INSURANCE_STATUS_CURRENT",
  Past = "OTHER_INSURANCE_STATUS_PAST",
}

export type BeneInfoCobaType =
  | "COBA_TYPE_OTHER"
  | "COBA_TYPE_SUPPLEMENTAL"
  | "COBA_TYPE_MEDIGAP"
  | "COBA_TYPE_TRICARE"
  | "COBA_TYPE_MEDICAID"
  | "COBA_TYPE_CHAMPVA";

export type BeneInfoOtherInsurance = {
  contractor_number: string;
  insurer_name: string;
  insurer_address_line_1: string;
  insurer_address_line_2: string;
  insurer_city: string;
  insurer_state: string;
  insurer_zip: string;
  policy_number: string;
  effective_date: string;
  termination_date: string;
  coba_number: number;
  coba_type: BeneInfoCobaType;
  plan_id: string;
  insurer_code: string;
};

export type RolloverCoverage = {
  contract_id: string;
  plan_id: string;
  segment_id: string;
  coverage_year: string;
  plan_name: string;
  plan_name_esp: string;
  status: RolloverStatus;
  plan_type: string;
  plan_status: RolloverPlanStatus;
  previous_plan: {
    contract_id: string;
    plan_id: string;
    segment_id: string;
    plan_type: string;
  };
};

/** Refine BeneInfoOtherInsurance for specific `coba_type` */
type MapMedicareCoverageType<
  T extends BeneInfoOtherInsurance,
  CobaType extends BeneInfoCobaType,
> = {
  [P in keyof T]: T[P] extends BeneInfoCobaType
    ? Extract<BeneInfoCobaType, CobaType>
    : T[P];
};

export type MedigapCoverage = MapMedicareCoverageType<
  BeneInfoOtherInsurance,
  "COBA_TYPE_MEDIGAP"
>;

/**
 * beneinfo/otherinsurance params
 * We currently only make use of these values.
 * If that changes, we should update this to be more inclusive.
 * Until then, it will enforce that we are not sending the wrong information.
 */
export type BeneInfoOtherInsuranceInfoParams = {
  coba_type?: CobaTypeFilter.Medigap;
  status?: OtherInsuranceStatus.Current;
};

/**
 * beneinfo/otherinsurance params (BEDAP v4)
 * We currently only make use of these values.
 * If that changes, we should update this to be more inclusive.
 * Until then, it will enforce that we are not sending the wrong information.
 */
export type BeneInfoOtherInsuranceInfoParamsV4 = {
  coba_type_filters?: ["COBA_TYPE_MEDIGAP"];
  status_filters?: ["COVERAGE_STATUS_CURRENT"];
};

export type BeneInfoOtherInsuranceResponseType = {
  other_insurances: BeneInfoOtherInsurance[];
};

/** Refine BeneInfoOtherInsuranceResponseType for specific `coba_type` */
type MapOtherInsuranceResponseType<
  T extends BeneInfoOtherInsuranceResponseType,
  InsuranceType extends BeneInfoOtherInsurance,
> = {
  [P in keyof T]: InsuranceType[];
};

export type MedigapCoveragesResponseType = MapOtherInsuranceResponseType<
  BeneInfoOtherInsuranceResponseType,
  MedigapCoverage
>;

export enum PlanCoverageStatus {
  Current,
  Future,
  Pending,
}

export enum CoverageType {
  MAPD = "MEDICARE_ADVANTAGE_PLAN",
  PDP = "PRESCRIPTION_DRUG_PLAN",
  MedigapPolicy = "MEDIGAP_POLICY",
  selectionNotMade = "SELECTION_NOT_MADE",
  OriginalMedicare = "ORIGINAL_MEDICARE",
  learnMore = "LEARN_MORE",
}

export interface GetMedigapPricingYearFlags extends LDFlagSet {
  feEnableNextYearMedigapPricing: boolean;
  mctCurrentPlanYear: number;
}

export enum ManagedQueryParameters {
  YEAR = "year",
  LANG = "lang",
}

export enum DismissibleAlert {
  M3PHighDrugCosts,
  MustHaveProvider,
}

export interface LogoutParams {
  /**
   * controls logging out MCTAPI at `/beneinfo/logout` (defaults to `false`)
   * - If `true`, no MCTAPI logout call is made
   * - If `false`, an attempt is made to log out MCTAPI
   */
  apiHasExpired?: boolean;
  /**
   * controls the toast type on redirect from `/sso/logout` (defaults to `false`)
   * - If `true`, the user will see a "Session Expired" toast after logout
   * - If `false`, the user will see a "Logged Out" toast after logout
   */
  sessionHasExpired?: boolean;
  /**
   * Any callback to be called during the logout process.
   * Run before calling `RESET_STATE` and erasing the `sid` cookie
   */
  onLogout?: () => void;
}

/** Component-specific types that need to be used outside of components, e.g., in helpers */
export * from "./components";

/** Utility types / helpers */
export * from "./helpers";
